import { css } from '@styled/css';
import { Box, Flex } from '@styled/jsx';
import { Auth } from 'aws-amplify';
import { useEffect, useState } from 'react';

import { BASE_URL_KEY, LOCALHOST_BASE_URL, REMOTE_BASE_URL } from '../env_constants';

export default function DeveloperMenu() {
  const [modalOpen, setModalOpen] = useState(false);
  const [apiEnv, setApiEnv] = useState<'remote' | 'local'>('remote');
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [currentToken, setCurrentToken] = useState<string>('Loading...');

  const orgId = 'org_1234567890abcdef';
  const workspaceId = 'ws_abcdef1234567890';

  useEffect(() => {
    const stored = (localStorage.getItem('apiEnv') as 'remote' | 'local' | null) ?? null;
    if (stored === 'remote' || stored === 'local') {
      setApiEnv(stored);
    }
  }, []);

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const session = await Auth.currentSession();
        const jwt = session.getIdToken().getJwtToken();
        setCurrentToken(jwt);
      } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.error('Failed to get current session:', error);
        setCurrentToken('Unable to retrieve token');
      }
    };

    void fetchToken();
  }, []);

  const save = () => {
    localStorage.setItem('apiEnv', apiEnv);
    localStorage.setItem(BASE_URL_KEY, apiEnv === 'local' ? LOCALHOST_BASE_URL : REMOTE_BASE_URL);
    setModalOpen(false);
    window.location.reload();
  };

  const handleCopy = (text: string, label: string) => {
    void navigator.clipboard.writeText(text).then(
      () => {
        setCopyFeedback(`${label} copied!`);
        setTimeout(() => setCopyFeedback(null), 2000);
      },
      () => {
        setCopyFeedback('Failed to copy');
        setTimeout(() => setCopyFeedback(null), 2000);
      }
    );
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    // Only close if clicking directly on the backdrop, not the dialog content
    if (e.target === e.currentTarget) {
      setModalOpen(false);
    }
  };

  const handleBackdropKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setModalOpen(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setModalOpen(true)}
        aria-label="Settings"
        aria-haspopup="dialog"
        aria-expanded={modalOpen}
        className={css({
          bg: 'rgba(255,255,255,0.15)',
          border: 'none',
          borderRadius: 'md',
          cursor: 'pointer',
          fontSize: 'lg',
          p: 2,
          lineHeight: 1,
          transition: 'all 0.15s',
          _hover: { bg: 'rgba(255,255,255,0.25)' },
        })}
      >
        ⚙️
      </button>

      {modalOpen && (
        <Flex
          role="presentation"
          position="fixed"
          inset={0}
          bg="rgba(0, 0, 0, 0.5)"
          alignItems="center"
          justifyContent="center"
          zIndex={1000}
          onClick={handleBackdropClick}
          onKeyDown={handleBackdropKeyDown}
        >
          <Box
            role="dialog"
            aria-modal="true"
            aria-labelledby="settings-title"
            bg="white"
            borderRadius="xl"
            border="none"
            boxShadow="xl"
            maxW="500px"
            w="90%"
            maxH="90vh"
            overflow="auto"
            position="relative"
            p={0}
          >
            <Box
              px={6}
              py={5}
              borderBottom="1px solid"
              borderColor="slate.200"
            >
              <h2
                id="settings-title"
                className={css({
                  m: 0,
                  fontSize: 'xl',
                  fontWeight: 'semibold',
                  color: 'slate.900',
                })}
              >
                Settings
              </h2>
            </Box>

            <Flex
              direction="column"
              gap={6}
              p={6}
            >
              <Box>
                <h3
                  className={css({
                    m: 0,
                    mb: 3,
                    fontSize: 'xs',
                    fontWeight: 'bold',
                    color: 'slate.500',
                    textTransform: 'uppercase',
                    letterSpacing: 'wider',
                  })}
                >
                  API Environment
                </h3>
                <Flex
                  direction="column"
                  gap={2}
                >
                  <label
                    htmlFor="api-env-select"
                    className={css({
                      fontSize: 'sm',
                      fontWeight: 'medium',
                      color: 'slate.700',
                    })}
                  >
                    Environment
                  </label>
                  <select
                    id="api-env-select"
                    value={apiEnv}
                    onChange={(e) => setApiEnv(e.target.value as 'remote' | 'local')}
                    className={css({
                      w: 'full',
                      px: 3,
                      py: 2.5,
                      pr: 10,
                      border: '1px solid',
                      borderColor: 'slate.300',
                      borderRadius: 'md',
                      fontSize: 'sm',
                      bg: 'white',
                      color: 'slate.700',
                      cursor: 'pointer',
                      outline: 'none',
                      appearance: 'none',
                      backgroundImage:
                        "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 12px center',
                      _focus: {
                        borderColor: 'blue.500',
                        boxShadow: '0 0 0 3px var(--colors-blue-100)',
                      },
                    })}
                  >
                    <option value="remote">Remote</option>
                    <option value="local">Local</option>
                  </select>
                </Flex>
              </Box>

              <hr
                className={css({
                  border: 'none',
                  borderTop: '1px solid',
                  borderColor: 'slate.200',
                  m: 0,
                })}
              />

              <Box>
                <h3
                  className={css({
                    m: 0,
                    mb: 4,
                    fontSize: 'xs',
                    fontWeight: 'bold',
                    color: 'slate.500',
                    textTransform: 'uppercase',
                    letterSpacing: 'wider',
                  })}
                >
                  Authentication & IDs
                </h3>

                <Flex
                  direction="column"
                  gap={4}
                >
                  <Flex
                    direction="column"
                    gap={2}
                  >
                    <label
                      htmlFor="current-token"
                      className={css({
                        fontSize: 'sm',
                        fontWeight: 'medium',
                        color: 'slate.700',
                      })}
                    >
                      Current Token
                    </label>
                    <Flex gap={2}>
                      <input
                        id="current-token"
                        type="text"
                        value={currentToken}
                        readOnly
                        className={css({
                          flex: 1,
                          minW: 0,
                          px: 3,
                          py: 2.5,
                          border: '1px solid',
                          borderColor: 'slate.300',
                          borderRadius: 'md',
                          fontSize: 'sm',
                          fontFamily: 'mono',
                          bg: 'slate.50',
                          color: 'slate.700',
                          outline: 'none',
                          _focus: {
                            borderColor: 'blue.500',
                            boxShadow: '0 0 0 3px var(--colors-blue-100)',
                          },
                        })}
                      />
                      <button
                        onClick={() => handleCopy(currentToken, 'Token')}
                        aria-label="Copy token"
                        className={css({
                          bg: 'slate.100',
                          border: '1px solid',
                          borderColor: 'slate.300',
                          borderRadius: 'md',
                          px: 3,
                          py: 2.5,
                          cursor: 'pointer',
                          fontSize: 'md',
                          lineHeight: 1,
                          transition: 'all 0.15s',
                          _hover: { bg: 'slate.200', borderColor: 'slate.400' },
                        })}
                      >
                        📋
                      </button>
                    </Flex>
                  </Flex>

                  <Flex
                    direction="column"
                    gap={2}
                  >
                    <label
                      htmlFor="org-id"
                      className={css({
                        fontSize: 'sm',
                        fontWeight: 'medium',
                        color: 'slate.700',
                      })}
                    >
                      Organization ID
                    </label>
                    <Flex gap={2}>
                      <input
                        id="org-id"
                        type="text"
                        value={orgId}
                        readOnly
                        className={css({
                          flex: 1,
                          minW: 0,
                          px: 3,
                          py: 2.5,
                          border: '1px solid',
                          borderColor: 'slate.300',
                          borderRadius: 'md',
                          fontSize: 'sm',
                          fontFamily: 'mono',
                          bg: 'slate.50',
                          color: 'slate.700',
                          outline: 'none',
                          _focus: {
                            borderColor: 'blue.500',
                            boxShadow: '0 0 0 3px var(--colors-blue-100)',
                          },
                        })}
                      />
                      <button
                        onClick={() => handleCopy(orgId, 'Organization ID')}
                        aria-label="Copy organization ID"
                        className={css({
                          bg: 'slate.100',
                          border: '1px solid',
                          borderColor: 'slate.300',
                          borderRadius: 'md',
                          px: 3,
                          py: 2.5,
                          cursor: 'pointer',
                          fontSize: 'md',
                          lineHeight: 1,
                          transition: 'all 0.15s',
                          _hover: { bg: 'slate.200', borderColor: 'slate.400' },
                        })}
                      >
                        📋
                      </button>
                    </Flex>
                  </Flex>

                  <Flex
                    direction="column"
                    gap={2}
                  >
                    <label
                      htmlFor="workspace-id"
                      className={css({
                        fontSize: 'sm',
                        fontWeight: 'medium',
                        color: 'slate.700',
                      })}
                    >
                      Workspace ID
                    </label>
                    <Flex gap={2}>
                      <input
                        id="workspace-id"
                        type="text"
                        value={workspaceId}
                        readOnly
                        className={css({
                          flex: 1,
                          minW: 0,
                          px: 3,
                          py: 2.5,
                          border: '1px solid',
                          borderColor: 'slate.300',
                          borderRadius: 'md',
                          fontSize: 'sm',
                          fontFamily: 'mono',
                          bg: 'slate.50',
                          color: 'slate.700',
                          outline: 'none',
                          _focus: {
                            borderColor: 'blue.500',
                            boxShadow: '0 0 0 3px var(--colors-blue-100)',
                          },
                        })}
                      />
                      <button
                        onClick={() => handleCopy(workspaceId, 'Workspace ID')}
                        aria-label="Copy workspace ID"
                        className={css({
                          bg: 'slate.100',
                          border: '1px solid',
                          borderColor: 'slate.300',
                          borderRadius: 'md',
                          px: 3,
                          py: 2.5,
                          cursor: 'pointer',
                          fontSize: 'md',
                          lineHeight: 1,
                          transition: 'all 0.15s',
                          _hover: { bg: 'slate.200', borderColor: 'slate.400' },
                        })}
                      >
                        📋
                      </button>
                    </Flex>
                  </Flex>
                </Flex>
              </Box>
            </Flex>

            <Flex
              justify="flex-end"
              gap={3}
              px={6}
              py={4}
              borderTop="1px solid"
              borderColor="slate.200"
              bg="slate.50"
            >
              <button
                onClick={() => setModalOpen(false)}
                className={css({
                  bg: 'white',
                  border: '1px solid',
                  borderColor: 'slate.300',
                  py: 2.5,
                  px: 5,
                  borderRadius: 'md',
                  cursor: 'pointer',
                  fontSize: 'sm',
                  fontWeight: 'medium',
                  color: 'slate.700',
                  transition: 'all 0.15s',
                  _hover: { bg: 'slate.100', borderColor: 'slate.400' },
                })}
              >
                Close
              </button>
              <button
                onClick={save}
                className={css({
                  bg: 'blue.500',
                  color: 'white',
                  border: 'none',
                  py: 2.5,
                  px: 5,
                  borderRadius: 'md',
                  cursor: 'pointer',
                  fontSize: 'sm',
                  fontWeight: 'medium',
                  transition: 'all 0.15s',
                  _hover: { bg: 'blue.600' },
                })}
              >
                Save Environment
              </button>
            </Flex>

            {copyFeedback && (
              <Box
                position="absolute"
                bottom={6}
                left="50%"
                transform="translateX(-50%)"
                bg="slate.800"
                color="white"
                px={4}
                py={2}
                borderRadius="md"
                fontSize="sm"
                fontWeight="medium"
                boxShadow="lg"
              >
                {copyFeedback}
              </Box>
            )}
          </Box>
        </Flex>
      )}
    </>
  );
}
